import { toBoundingBox, toBounds, unionBounds } from '@/utils/bounds-kit';
import type { BoundingBox, Bounds, ILayerProps, IVectorLayerProps } from '@/version-types';

/**
 * 将 Figma 模糊值换算为 3σ 安全绘制边界。
 *
 * Figma 的 SVG 标准差为 blur / 2；扩展到 3σ 可避免 2σ 处残余像素形成硬边。
 *
 * @param blur - Figma 面板中的模糊值。
 * @returns 单边需要预留的绘制空间。
 */
export const getVectorBlurExpansion = (blur: number): number => Math.max(0, blur) * 1.5;

/**
 * 计算翻转前且不包含特效的基础 Render Box。
 *
 * 翻转需要在主体与特效完成合成后统一应用，因此这里不能提前镜像主体范围。
 *
 * @param layer - 当前矢量图层。
 * @returns 应用缩放、旋转和偏移后，应用翻转与特效前的轴对齐包围盒。
 */
export const getVectorBaseRenderBox = (layer: ILayerProps): BoundingBox => {
    const { width, height } = layer;
    const props = layer.data as IVectorLayerProps;
    const transform = props.imageTransform;

    if (!transform) {
        return { left: 0, top: 0, width, height };
    }

    const { widthRatio = 1, heightRatio = 1, scale = 1, rotate = 0, offsetX = 0, offsetY = 0 } = transform;
    const scaledWidth = width * widthRatio * scale;
    const scaledHeight = height * heightRatio * scale;
    const centerX = width / 2 + offsetX * scaledWidth;
    const centerY = height / 2 + offsetY * scaledHeight;
    const radian = (rotate * Math.PI) / 180;
    const cos = Math.cos(radian);
    const sin = Math.sin(radian);
    const halfWidth = scaledWidth / 2;
    const halfHeight = scaledHeight / 2;
    const corners = [
        { x: -halfWidth, y: -halfHeight },
        { x: halfWidth, y: -halfHeight },
        { x: halfWidth, y: halfHeight },
        { x: -halfWidth, y: halfHeight },
    ].map(({ x, y }) => ({
        x: centerX + x * cos - y * sin,
        y: centerY + x * sin + y * cos,
    }));
    const left = Math.min(...corners.map((point) => point.x));
    const top = Math.min(...corners.map((point) => point.y));
    const right = Math.max(...corners.map((point) => point.x));
    const bottom = Math.max(...corners.map((point) => point.y));

    return {
        left,
        top,
        width: right - left,
        height: bottom - top,
    };
};

/**
 * 按当前图形主体与所有可见特效计算最终 Render Box。
 *
 * @param layer - 当前矢量图层。
 * @param baseRenderBox - 可选的翻转前基础范围；未传入时从 imageTransform 计算。
 * @param additionalRenderBox - 可选的文字等宿主级视觉范围；在共同 outer flip 前参与并集。
 * @returns runtime、preview 与导出共同使用的最终局部渲染范围。
 */
export const getVectorRenderBox = (
    layer: ILayerProps,
    baseRenderBox: BoundingBox = getVectorBaseRenderBox(layer),
    additionalRenderBox?: BoundingBox,
): BoundingBox => {
    const props = layer.data as IVectorLayerProps;
    const boundsList: Bounds[] = [toBounds(baseRenderBox)];
    const baseBounds = toBounds(baseRenderBox);

    if (props.blur > 0) {
        const expansion = getVectorBlurExpansion(props.blur);

        boundsList.push({
            left: baseBounds.left - expansion,
            top: baseBounds.top - expansion,
            right: baseBounds.right + expansion,
            bottom: baseBounds.bottom + expansion,
        });
    }

    const shadow = props.effect?.shadow?.[0];
    if (shadow && !shadow.hidden) {
        const expansion = getVectorBlurExpansion(shadow.blur);

        boundsList.push({
            left: baseBounds.left + shadow.x - expansion,
            top: baseBounds.top + shadow.y - expansion,
            right: baseBounds.right + shadow.x + expansion,
            bottom: baseBounds.bottom + shadow.y + expansion,
        });
    }

    const reflection = props.effect?.reflection?.[0];
    if (reflection && !reflection.hidden) {
        const reflectionHeight = (baseRenderBox.height * Math.max(0, Math.min(100, reflection.size))) / 100;
        if (reflectionHeight > 0) {
            const expansion = getVectorBlurExpansion(reflection.blur);
            const reflectionTop = baseBounds.bottom + reflection.y;

            boundsList.push({
                left: baseBounds.left - expansion,
                top: reflectionTop - expansion,
                right: baseBounds.right + expansion,
                bottom: reflectionTop + reflectionHeight + expansion,
            });
        }
    }

    if (additionalRenderBox) boundsList.push(toBounds(additionalRenderBox));

    const renderBox = toBoundingBox(unionBounds(...boundsList) ?? baseBounds);
    const { flipH = false, flipV = false } = props.imageTransform || {};

    // builder 会在主体和特效合成后围绕图层中心执行翻转；Render Box 必须使用相同顺序，
    // 否则投影或倒影翻到另一侧后仍会沿用翻转前的绘制范围并被裁切。
    return {
        left: flipH ? layer.width - renderBox.left - renderBox.width : renderBox.left,
        top: flipV ? layer.height - renderBox.top - renderBox.height : renderBox.top,
        width: renderBox.width,
        height: renderBox.height,
    };
};
